import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, MessageSquare, FileText, Phone } from 'lucide-react';
import ComplianceFooter from './ComplianceFooter';

const CompletionStep: React.FC = () => {
  const [reviewComplete, setReviewComplete] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCompleteReview = async () => {
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setReviewComplete(true);
    } catch (error) {
      console.error('Error completing review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionSelect = (action: string) => {
    setSelectedAction(action);
  };

  const nextSteps = [
    {
      id: 'call',
      title: 'Schedule a Call',
      description: 'Book a meeting to discuss your findings and next steps',
      icon: Phone,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 'questions',
      title: 'Submit Questions',
      description: 'Upload your questions or request additional information',
      icon: MessageSquare,
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'access',
      title: 'Request Deeper Access',
      description: 'Request access to additional confidential materials',
      icon: FileText,
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-4xl mx-auto"
    >
      <div className="card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <CheckCircle className="w-8 h-8" color="#cba864" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Confirmation & Next Steps
          </h2>
          <p className="text-neutral-600">
            Thank you for your time and for reviewing this exclusive project. Our executive team will follow up shortly to discuss any insights, address questions, and outline potential next steps for further engagement.
          </p>
        </div>

        {!reviewComplete ? (
          <div className="space-y-6">
            {/* Review Summary */}
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" color="#cba864" />
                Review Summary
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-neutral-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600">2</div>
                  <div className="text-sm text-neutral-600">Documents Reviewed</div>
                </div>
                <div className="text-center p-4 bg-neutral-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600">45m</div>
                  <div className="text-sm text-neutral-600">Time Spent</div>
                </div>
                <div className="text-center p-4 bg-neutral-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600">2</div>
                  <div className="text-sm text-neutral-600">Downloads</div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-neutral-600">
                <p>• Last accessed: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
                <p>• Session duration: 45 minutes</p>
                <p>• Documents viewed: Financials</p>
              </div>
            </div>

            {/* Completion Button */}
            <div className="text-center">
              <button
                onClick={handleCompleteReview}
                disabled={isSubmitting}
                className="btn-primary flex items-center justify-center mx-auto"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark Review Complete
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Success Message */}
            <div className="text-center p-6 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-green-800 mb-2">
                Review Completed Successfully!
              </h3>
              <p className="text-green-700">
                Thank you for completing your review. Our team will be in touch within 24 hours.
              </p>
            </div>

            {/* Next Steps */}
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" color="#cba864" />
                What Would You Like to Do Next?
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {nextSteps.map((step) => (
                  <motion.button
                    key={step.id}
                    onClick={() => handleActionSelect(step.id)}
                    className={`p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${
                      selectedAction === step.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-primary-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-3`} style={{backgroundColor: '#faf0e3', color: '#cba864'}}>
                      <step.icon className="w-5 h-5" color="#cba864" />
                    </div>
                    <h4 className="font-medium text-neutral-900 mb-1">{step.title}</h4>
                    <p className="text-sm text-neutral-600">{step.description}</p>
                  </motion.button>
                ))}
              </div>

              {selectedAction && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-neutral-50 rounded-lg"
                >
                  <h4 className="font-medium text-neutral-900 mb-2">
                    {nextSteps.find(s => s.id === selectedAction)?.title}
                  </h4>
                  <p className="text-sm text-neutral-600 mb-4">
                    {nextSteps.find(s => s.id === selectedAction)?.description}
                  </p>
                  
                  <div className="flex space-x-3">
                    <button className="btn-primary">
                      Proceed
                    </button>
                    <button 
                      onClick={() => setSelectedAction('')}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Contact Information */}
            <div className="border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Need Immediate Assistance?
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5" color="#cba864" />
                  <div>
                    <p className="font-medium text-neutral-900">Phone Support</p>
                    <p className="text-sm text-neutral-600">+1 (555) 123-4567</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <MessageSquare className="w-5 h-5" color="#cba864" />
                  <div>
                    <p className="font-medium text-neutral-900">Email Support</p>
                    <p className="text-sm text-neutral-600">support@dataroom.com</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <ComplianceFooter />
      </div>
    </motion.div>
  );
};

export default CompletionStep; 